/**
 * Pure formatting shared by Mail and Chat -- kept apart from any component so
 * it can be tested without a client, a DOM, or a network.
 */

export const DAY_MS = 24 * 60 * 60 * 1000;

export function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** A row's own timestamp: the time today, "Yesterday", a weekday name within
 *  the last week, or a short date beyond that -- the same ladder every mail
 *  or chat client uses, because the alternative is either always-today's
 *  clock (a lie) or always a full date (unreadable at a glance for the 90%
 *  of rows that are this week). */
export function formatWhen(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const days = Math.floor((startOfDay(now) - startOfDay(at)) / DAY_MS);
  if (days === 0) return clockTime(at);
  if (days === 1) return 'Yesterday';
  if (days > 1 && days < 7) return WEEKDAYS[at.getDay()] ?? '';
  return shortDate(at, days);
}

/** `en-GB`, pinned like the grid and `Agenda`: the browser's own locale can
 *  print "24:05" for ten past midnight. */
function clockTime(at: Date): string {
  return at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** "7 Sep", with the year once it is about eleven months back, so last
 *  September does not read like this one. */
function shortDate(at: Date, daysAgo: number): string {
  const date = `${at.getDate()} ${MONTHS[at.getMonth()] ?? ''}`;
  return daysAgo > 330 ? `${date} ${at.getFullYear()}` : date;
}

/** A message's own header: the same ladder as `formatWhen`, but always with
 *  the clock time -- "Today 07:41", "Mon 14:02", "7 Sep 09:05". */
export function formatMessageWhen(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const days = Math.floor((startOfDay(now) - startOfDay(at)) / DAY_MS);
  const time = clockTime(at);
  if (days === 0) return `Today ${time}`;
  if (days === 1) return `Yesterday ${time}`;
  if (days > 1 && days < 7) return `${WEEKDAYS[at.getDay()] ?? ''} ${time}`;
  return `${shortDate(at, days)} ${time}`;
}
